import { LinearProgress } from '@mui/material';
import { lazy, Suspense } from 'react';
import { PrivateRoute } from 'shell/components/shared/private-route';
import { SystemAuthorizationsList } from 'shell/authorizations/system';
import { FamilyAuthorizationsList } from 'shell/authorizations/family';
import { CohabitationAuthorizationsList } from 'shell/authorizations/cohabitation';
import { FrontOfficeAuthorizationsList } from 'shell/authorizations/front-office';
import { PersonAuthorizationsList } from 'shell/authorizations/person';
import { NotFound } from 'shell/error/components/error-not-found';
import Layout from 'shell/pages/layout';
import { RoutesConfig } from './routes-config';
import { SubscriptionAuthorizationsList } from 'shell/authorizations/subscription';
import { MutationAuthorizationsList } from 'shell/authorizations/mutation';
import { CancellationsAuthorizationsList } from 'shell/authorizations/cancellations';
// import { CivilStatusAuthorizationsList } from 'shell/authorizations/civil-status';
import { PracticeAuthorizationsList } from 'shell/authorizations/practice';
import { MassiveLoaderAuthorizationsList } from 'shell/authorizations/massive-loader';
import { MassiveLoaderMultiYearAuthorizationsList } from 'shell/authorizations/massive-loader-multi-year';
import { MilitaryDraftAuthorizationsList } from 'shell/authorizations/military-draft';
import { MunicipalMessengerAuthorizationsList } from 'shell/authorizations/municipal-messenger';
import { ElectoralAuthorizationsList } from 'shell/authorizations/electoral';
import { HistorianAuthorizationList } from 'shell/authorizations/historian';
import { ResidentAuthorizationsList } from 'shell/authorizations/resident';
import { RegisterOfficeOperationsList } from 'shell/authorizations/register-office-operations';

interface CreateShellRoutesParams {
  onLoadingStart: () => void;
  onLoadingEnd: () => void;
}

export const createShellRoutes = ({
  onLoadingStart,
  onLoadingEnd,
}: CreateShellRoutesParams): RoutesConfig => {
  const LazyHome = lazy(() => {
    onLoadingStart();
    return import('../pages').finally(() => onLoadingEnd());
  });

  const LazyAuth = lazy(() => {
    onLoadingStart();
    return import('authentication/router').finally(() => onLoadingEnd());
  });

  const LazyPerson = lazy(() => {
    onLoadingStart();
    return import('person/router/index').finally(() => onLoadingEnd());
  });

  const LazyFamily = lazy(() => {
    onLoadingStart();
    return import('family/router/index').finally(() => onLoadingEnd());
  });

  const LazyCohabitation = lazy(() => {
    onLoadingStart();
    return import('cohabitation/router/index').finally(() => onLoadingEnd());
  });

  const LazyNotificationsFromCivilStatus = lazy(() => {
    onLoadingStart();
    return import('notifications-from-civil-status/router/index').finally(() => onLoadingEnd());
  });

  const LazyFrontOffice = lazy(() => {
    onLoadingStart();
    return import('front-office/router/index').finally(() => onLoadingEnd());
  });

  const LazySystem = lazy(() => {
    onLoadingStart();
    return import('system/router/index').finally(() => onLoadingEnd());
  });

  const LazySubscription = lazy(() => {
    onLoadingStart();
    return import('subscription/router/index').finally(() => onLoadingEnd());
  });

  const LazyMutation = lazy(() => {
    onLoadingStart();
    return import('mutation/router/index').finally(() => onLoadingEnd());
  });

  const LazyCancellation = lazy(() => {
    onLoadingStart();
    return import('cancellations/router/index').finally(() => onLoadingEnd());
  });

  const LazyCivilStatus = lazy(() => {
    onLoadingStart();
    return import('civil-status/router/index').finally(() => onLoadingEnd());
  });

  const LazyPractice = lazy(() => {
    onLoadingStart();
    return import('practice/router/index').finally(() => onLoadingEnd());
  });

  const LazyMassiveLoader = lazy(() => {
    onLoadingStart();
    return import('massive-loader/router/index').finally(() => onLoadingEnd());
  });

  const LazyMassiveLoaderMultiYear = lazy(() => {
    onLoadingStart();
    return import('massive-loader-multi-year/router/index').finally(() => onLoadingEnd());
  });

  const LazyMilitaryDraft = lazy(() => {
    onLoadingStart();
    return import('military-draft/router/index').finally(() => onLoadingEnd());
  });

  const LazyMunicipalMessenger = lazy(() => {
    onLoadingStart();
    return import('municipal-messenger/router/index').finally(() => onLoadingEnd());
  });

  const LazyHistorian = lazy(() => {
    onLoadingStart();
    return import('historian/router/index').finally(() => onLoadingEnd());
  });

  const LazyElectoral = lazy(() => {
    onLoadingStart();
    return import('electoral/router/index').finally(() => onLoadingEnd());
  });

  const LazyResident = lazy(() => {
    onLoadingStart();
    return import('resident/router/index').finally(() => onLoadingEnd());
  });

  const LazyRegisterOfficeOperations = lazy(() => {
    onLoadingStart();
    return import('register-office-operations/router/index').finally(() => onLoadingEnd());
  });

  return [
    {
      path: '',
      element: <PrivateRoute />,
      children: [
        {
          path: '',
          element: <Layout />,
          children: [
            {
              path: '',
              element: <LazyHome />,
            },
            {
              path: '/persona/*',
              element: <LazyPerson />,
              authorizations: PersonAuthorizationsList,
            },
            {
              path: '/famiglia/*',
              element: <LazyFamily />,
              authorizations: FamilyAuthorizationsList,
            },
            {
              path: '/convivenza/*',
              element: <LazyCohabitation />,
              authorizations: CohabitationAuthorizationsList,
            },
            {
              path: '/notifiche-da-stato-civile/*',
              element: <LazyNotificationsFromCivilStatus />,
              // TODO authorizations: FrontOfficeAuthorizationsList,
            },
            {
              path: '/sportello/*',
              element: <LazyFrontOffice />,
              authorizations: FrontOfficeAuthorizationsList,
            },
            {
              path: '/sistema/*',
              element: <LazySystem />,
              authorizations: SystemAuthorizationsList,
            },
            {
              path: '/iscrizioni/*',
              element: <LazySubscription />,
              authorizations: SubscriptionAuthorizationsList,
            },
            {
              path: '/mutazioni/*',
              element: <LazyMutation />,
              authorizations: MutationAuthorizationsList,
            },
            {
              path: '/cancellazioni/*',
              element: <LazyCancellation />,
              authorizations: CancellationsAuthorizationsList,
            },
            {
              path: '/stato-civile/*',
              element: <LazyCivilStatus />,
              // authorizations: CivilStatusAuthorizationsList, // mettere le nuove autorizzazione
            },
            {
              path: '/pratiche/*',
              element: <LazyPractice />,
              authorizations: PracticeAuthorizationsList,
            },
            {
              path: '/caricatore-massivo-multi-annuale/*',
              element: <LazyMassiveLoaderMultiYear />,
              authorizations: MassiveLoaderMultiYearAuthorizationsList,
            },
            {
              path: '/caricatore-massivo/*',
              element: <LazyMassiveLoader />,
              authorizations: MassiveLoaderAuthorizationsList,
            },
            {
              path: '/leva/*',
              element: <LazyMilitaryDraft />,
              authorizations: MilitaryDraftAuthorizationsList,
            },
            {
              path: '/messi/*',
              element: <LazyMunicipalMessenger />,
              authorizations: MunicipalMessengerAuthorizationsList,
            },
            {
              path: '/storico/*',
              element: <LazyHistorian />,
              authorizations: HistorianAuthorizationList,
            },
            {
              path: '/elettorale/*',
              element: <LazyElectoral />,
              authorizations: ElectoralAuthorizationsList,
            },
            {
              path: '/residenti/*',
              element: <LazyResident />,
              authorizations: ResidentAuthorizationsList,
            },
            {
              path: '/anagrafe-comunale/operazioni/*',
              element: <LazyRegisterOfficeOperations />,
              authorizations: RegisterOfficeOperationsList,
            },
            {
              element: <NotFound />,
              path: '*',
              hidden: true,
            },
          ],
        },
      ],
    },
    {
      path: '/autenticazione/*',
      element: (
        <Suspense fallback={<LinearProgress />}>
          <LazyAuth />
        </Suspense>
      ),
    },
  ];
};
